import React, { useEffect, useRef, useState } from "react";
import {
  FieldArray,
  FieldControl,
  FieldGroup,
  FormArray,
  FormBuilder,
} from "react-reactive-form";
import { ReactComponent as PlusIcon } from "../assets/images/icons/plus.svg";
import { ReactComponent as BinIcon } from "../assets/images/icons/bin.svg";
import { CButton, CFormInput, CFormSelect } from "@coreui/react";
import QuestionItemInfo from "./QuestionItemInfo";
import useQuestions from "../hooks/useQuestions";
import QuillArrayFieldEditor from "./QuillArrayFieldEditor";
import RichHtmlSelect from "./RichHtmlSelect";

// Self-managing dropdown for comparison selects whose choices come from a
// sibling FormArray (e.g. qTgNames, qPkAnalytes, qPdTreatmentGroupsComp) that
// lives outside this FieldControl's subscription scope. react-reactive-form's
// <Field> uses `shouldComponentUpdate => false` and only re-renders when its
// own control's stateChanges fires, so we cannot rely on parent re-renders to
// refresh the options. This component subscribes to the whole form and
// re-renders itself as soon as its computed choices change. Subscribing to the
// root (rather than named controls) also survives source controls being
// replaced via setControl after mount.
//
// When a selected value disappears from the source (e.g. a 4a column header is
// removed), the control is cleared too; otherwise the dropdown shows the
// placeholder but the stale value is still submitted. The check is deferred to
// a microtask because page restores empty and refill the source arrays row by
// row, synchronously; by the time the microtask runs the source is complete
// again, so only values that are genuinely gone get cleared.
const ComparisonRichHtmlSelect = ({
  control,
  handler,
  fieldName,
  formGroup,
  getChoicesForField,
}) => {
  const [, tick] = useState(0);
  const getChoicesRef = useRef(getChoicesForField);
  getChoicesRef.current = getChoicesForField;
  const choices = getChoicesForField(fieldName) || [];
  const choicesKeyRef = useRef(JSON.stringify(choices));
  choicesKeyRef.current = JSON.stringify(choices);
  const choicesRef = useRef(choices);
  choicesRef.current = choices;
  // Choices as they were before the current batch of changes; null when no
  // stale-value check is pending.
  const pendingPrevChoicesRef = useRef(null);

  useEffect(() => {
    if (!formGroup?.valueChanges) return undefined;
    let active = true;
    const hasValue = (list, value) =>
      list.some(o => String(o.value) === String(value));

    const subscription = formGroup.valueChanges.subscribe(() => {
      const nextChoices = getChoicesRef.current(fieldName) || [];
      const nextKey = JSON.stringify(nextChoices);
      if (nextKey === choicesKeyRef.current) return;

      if (control && pendingPrevChoicesRef.current === null) {
        pendingPrevChoicesRef.current = choicesRef.current;
        Promise.resolve().then(() => {
          const prevChoices = pendingPrevChoicesRef.current || [];
          pendingPrevChoicesRef.current = null;
          if (!active) return;
          const value = control.value;
          if (
            value !== "" &&
            value !== null &&
            value !== undefined &&
            hasValue(prevChoices, value) &&
            !hasValue(getChoicesRef.current(fieldName) || [], value)
          ) {
            control.setValue("");
          }
        });
      }

      choicesKeyRef.current = nextKey;
      choicesRef.current = nextChoices;
      tick(v => v + 1);
    });
    return () => {
      active = false;
      subscription?.unsubscribe?.();
    };
  }, [formGroup, fieldName, control]);

  return <RichHtmlSelect options={choices} {...handler} />;
};

const QuestionsAnswersArray = ({
  questionIndex,
  showQuestionData = true,
  controlName,
  arrayControlName = false,
  questionsName = "",
  activator = false,
  formGroup,
  minInputs = 1,
  maxInputs = 999,
  arrayLabel,
  inputTextBoxes,
  inputTextBoxesAdditionalData,
  additionalNonArrayTextBoxControlName,
  marginAboveLabels = false,
  specialTimes = 0,
  placeholder,
  onBlur,
  onChange,
  error = false,
  required = false,
}) => {
  const { questionsData, updateTgNamesComparisonAdditionalData } =
    useQuestions();

  if (!questionsName) questionsName = controlName;

  // Row identity counter. This must be a ref, not state: <FieldArray> renders
  // through react-reactive-form's <Field>, which sets `shouldComponentUpdate`
  // to false and therefore keeps the render callback captured at mount time.
  // A state-based counter is frozen inside that stale closure, so every row
  // added from the "Add another" button would get the same meta.key and React
  // would reconcile the wrong row on delete.
  const keyCount = useRef(0);

  useEffect(() => {
    if (!formGroup || !controlName) return;

    let local_form_array;
    if (
      formGroup.contains(controlName) &&
      formGroup.get(controlName) instanceof FormArray
    ) {
      local_form_array = formGroup.get(controlName);
    } else {
      // warning: this will change the order in the parent form group, try to avoid reaching here
      local_form_array = new FormArray([]);

      formGroup.setControl(`${controlName}`, local_form_array);
      if (activator) addAnswerGroup(null, minInputs);
    }

    if (!local_form_array) return;

    if (activator !== undefined && activator !== null) {
      if (
        activator &&
        formGroup.get(controlName) &&
        formGroup.get(controlName).length === 0
      ) {
        // Add answer group when activator is truthy and the array is empty
        addAnswerGroup(null, minInputs);
      }
    }
  }, [activator, controlName, formGroup]);

  const addAnswerGroup = (e, minInputs = 1) => {
    if (e) e.preventDefault();

    const itemsControl = formGroup.get(controlName);

    while (minInputs--) {
      itemsControl.push(createItem());
    }
  };

  // Deletes by control identity, not by the index captured when the row was
  // rendered. The surrounding <FieldGroup> does not re-render when rows shift
  // up after a delete, so a captured index goes stale and would remove the
  // wrong row on the next click.
  const deleteAnswerGroup = (e, itemControl, index) => {
    e.preventDefault();

    const itemsControl = formGroup.get(controlName);

    if (!itemsControl) return;

    const currentIndex = Array.isArray(itemsControl.controls)
      ? itemsControl.controls.indexOf(itemControl)
      : -1;

    itemsControl.removeAt(currentIndex === -1 ? index : currentIndex);
  };

  // Creates the unique keys
  const getKey = () => `qaa-${keyCount.current++}`;

  // Rows added outside this component (e.g. save/restore helpers) already carry
  // a meta.key; anything else gets one lazily so React always has a stable
  // per-row identity to reconcile with.
  const getItemKey = itemControl => {
    if (itemControl.meta?.key === undefined || itemControl.meta?.key === null) {
      itemControl.meta = { ...(itemControl.meta || {}), key: getKey() };
    }

    return itemControl.meta.key;
  };

  const createItem = () => {
    const control = FormBuilder.group(
      inputTextBoxes.map(v => v[1]).reduce((a, b) => ({ ...a, [b]: [""] }), {})
    );

    // Adding key
    control.meta = {
      key: getKey(),
    };

    return control;
  };

  return (
    <>
      {activator && (
        <div className="questions-content__container">
          {questionIndex ? (
            <div className="questions-content__questionIndexContainer">
              <span className="questions-content__label-index">
                {questionIndex}
              </span>
            </div>
          ) : (
            ""
          )}
          <div
            className={
              questionsName === "qSubgroupNames"
                ? "questions-content__item special-space"
                : !marginAboveLabels
                  ? "questions-content__item"
                  : ""
            }>
            {showQuestionData && specialTimes === 0 && (
              <div
                className={
                  "questions-content__label questions-content__label--array"
                }>
                <span className={"questions-content__label-text"}>
                  {questionsData.current.questions[questionsName]}
                  {required && (
                    <span className="questions-content__required-asterisk">
                      {" "}
                      *
                    </span>
                  )}
                </span>
                <QuestionItemInfo
                  questionName={questionsName}
                  notes={questionsData.current.questionsInfo}
                />
              </div>
            )}
            {additionalNonArrayTextBoxControlName && (
              <>
                <div className="sublabels-wrapper">
                  <label className="sublabel">Parent Product</label>
                  <span className="empty-space-layout-helper"></span>
                </div>
                <div className={"questions-content__input"}>
                  <div className="questions-content__input-group">
                    <FieldControl
                      name={"qPKmetaboliteNames.ParentalProduct"}
                      render={({ handler }) => (
                        <>
                          <CFormInput
                            type="text"
                            name="ParentalProduct"
                            className={"input-text margin-bottom-text"}
                            {...handler()}
                          />
                        </>
                      )}
                    />
                    <span className="empty-space-layout-helper"></span>
                  </div>
                </div>
              </>
            )}
            {arrayLabel && (
              <div className="sublabels-wrapper">
                <label className="sublabel">{arrayLabel}</label>
                <span className="empty-space-layout-helper"></span>
              </div>
            )}
            {marginAboveLabels && (
              <span className={"empty-space-layout-helper"}></span>
            )}
            {questionsName === "qSubgroupNames" && (
              <label className={"main-label"}>{controlName}</label>
            )}
            {inputTextBoxes[0][0] !== "" && (
              <div
                className={
                  inputTextBoxes.length === 1
                    ? "sublabels-wrapper"
                    : "sublabels-wrapper two-col"
                }>
                {inputTextBoxes.map((textBox, i) => (
                  <label key={`${textBox[0]}-${i}`} className={"sublabel"}>
                    {textBox[0]}
                  </label>
                ))}
                <span className={"empty-space-layout-helper"}></span>
              </div>
            )}
            {formGroup &&
              formGroup.get &&
              formGroup.get(controlName) &&
              formGroup.get(controlName) instanceof FormArray && (
                <FieldArray
                  name={arrayControlName ? arrayControlName : controlName}
                  parent={formGroup}
                  render={({ controls }) => (
                    <>
                      <div className={"questions-content__input"}>
                        {controls &&
                          Array.isArray(controls) &&
                          controls.map((itemControl, iControl) => (
                            <div
                              key={getItemKey(itemControl)}
                              className={
                                inputTextBoxes.length === 1
                                  ? "questions-content__input-group"
                                  : "questions-content__input-group two-col"
                              }>
                              <FieldGroup
                                control={itemControl}
                                render={() => (
                                  <>
                                    {inputTextBoxes.map((textBox, i) => (
                                      <React.Fragment
                                        key={`${textBox[1]}-${i}`}>
                                        {textBox[2] === "richtext" && (
                                          <QuillArrayFieldEditor
                                            itemControl={itemControl}
                                            fieldName={textBox[1]}
                                          />
                                        )}
                                        {textBox[2] === "text" && (
                                          <>
                                            <FieldControl
                                              control={itemControl.get(
                                                textBox[1]
                                              )}
                                              name={textBox[1]}
                                              render={({ handler }) => {
                                                const h = handler();
                                                return (
                                                  <CFormInput
                                                    type="text"
                                                    name={textBox[1]}
                                                    className={`input-text ${error && !!error && "is-invalid"}`}
                                                    placeholder={
                                                      inputTextBoxes.length ===
                                                      1
                                                        ? placeholder
                                                        : undefined
                                                    }
                                                    {...h}
                                                    onChange={e => {
                                                      h.onChange?.(e);
                                                      onChange?.();
                                                    }}
                                                    onBlur={e => {
                                                      h.onBlur?.(e);
                                                      onBlur?.();
                                                    }}
                                                  />
                                                );
                                              }}
                                            />
                                          </>
                                        )}
                                        {textBox[2] === "select" && (
                                          <FieldControl
                                            name={textBox[1]}
                                            render={fieldControl => {
                                              const { handler } = fieldControl;
                                              if (
                                                inputTextBoxesAdditionalData ===
                                                "updateTgNamesComparisonAdditionalData"
                                              ) {
                                                return (
                                                  <ComparisonRichHtmlSelect
                                                    control={fieldControl}
                                                    handler={handler()}
                                                    fieldName={textBox[1]}
                                                    formGroup={formGroup}
                                                    getChoicesForField={name =>
                                                      updateTgNamesComparisonAdditionalData()[
                                                        name
                                                      ]
                                                    }
                                                  />
                                                );
                                              }
                                              if (
                                                typeof inputTextBoxesAdditionalData ===
                                                "function"
                                              ) {
                                                return (
                                                  <ComparisonRichHtmlSelect
                                                    control={fieldControl}
                                                    handler={handler()}
                                                    fieldName={textBox[1]}
                                                    formGroup={formGroup}
                                                    getChoicesForField={name =>
                                                      inputTextBoxesAdditionalData()[
                                                        name
                                                      ]
                                                    }
                                                  />
                                                );
                                              }
                                              const choices =
                                                inputTextBoxesAdditionalData[
                                                  textBox[1]
                                                ];
                                              return (
                                                <RichHtmlSelect
                                                  options={choices || []}
                                                  {...handler()}
                                                />
                                              );
                                            }}
                                          />
                                        )}
                                        {textBox[2] === "select_multiple" && (
                                          <>
                                            <FieldControl
                                              name={textBox[1]}
                                              render={({ handler }) => (
                                                <>
                                                  <CFormSelect
                                                    className={"input-select"}
                                                    {...handler()}>
                                                    <option>
                                                      - Choose value -
                                                    </option>
                                                    {typeof inputTextBoxesAdditionalData ===
                                                      "string" ||
                                                    inputTextBoxesAdditionalData ===
                                                      null ||
                                                    !Array.isArray(
                                                      inputTextBoxesAdditionalData
                                                    ) ? (
                                                      <>
                                                        <option
                                                          key={`${textBox[1]}-0`}
                                                          value={null}>
                                                          {"None"}
                                                        </option>
                                                        {inputTextBoxesAdditionalData ? (
                                                          <option
                                                            key={`${textBox[1]}-1`}
                                                            value={
                                                              inputTextBoxesAdditionalData
                                                            }>
                                                            {
                                                              inputTextBoxesAdditionalData
                                                            }
                                                          </option>
                                                        ) : null}
                                                      </>
                                                    ) : (
                                                      inputTextBoxesAdditionalData.map(
                                                        (option, i) => (
                                                          <option
                                                            key={`${textBox[1]}-${i}`}
                                                            value={
                                                              option.valueHelper
                                                            }>
                                                            {option.valueHelper}
                                                          </option>
                                                        )
                                                      )
                                                    )}
                                                  </CFormSelect>
                                                </>
                                              )}
                                            />
                                          </>
                                        )}
                                      </React.Fragment>
                                    ))}
                                    {iControl === 0 ||
                                    iControl === minInputs - 1 ? (
                                      <span
                                        className={
                                          "empty-space-layout-helper"
                                        }></span>
                                    ) : (
                                      <CButton
                                        color={"danger"}
                                        shape={"pill"}
                                        onClick={e => {
                                          deleteAnswerGroup(
                                            e,
                                            itemControl,
                                            iControl
                                          );
                                        }}>
                                        <BinIcon />
                                      </CButton>
                                    )}
                                  </>
                                )}
                              />
                            </div>
                          ))}
                      </div>
                      {controls &&
                        Array.isArray(controls) &&
                        controls.length < maxInputs && (
                          <CButton
                            color={"primary"}
                            variant={"outline"}
                            className={"btn-light"}
                            onClick={e => {
                              addAnswerGroup(e);
                            }}>
                            Add another <PlusIcon />
                          </CButton>
                        )}
                      {error && (
                        <div className="table-analysis-field-error">
                          {error}
                        </div>
                      )}
                    </>
                  )}
                />
              )}
          </div>
        </div>
      )}
    </>
  );
};

export default QuestionsAnswersArray;
